import { getAppEnv } from '@pbk-web/app-env';
import { useCurrentClient } from '@pbk-web/state';
import { Roles } from '@pbk-web/types';
import { hasPermission } from '@pbk-web/utils';
import { useMemo } from 'react';

import { TrainingMemberType } from '../models/Training';
import type { TrainingEvent } from '../models/TrainingEvent';
import { useMyTrainingPrograms } from './useMyTrainingPrograms';

type TrainingPermissions = {
  canManagePrograms: boolean;
  canViewReports: boolean;
  canCreateEvents: (trainingId: string) => boolean;
  canManageEvent: (trainingEvent: TrainingEvent) => boolean;
  isTrainerOnAny: boolean;
  isTrainerOn: (trainingId: string) => boolean;
  isTrainerOnEvent: (trainingEvent: TrainingEvent) => boolean;
  /** True while the trainer-program list is still being read — trainer flags are not yet meaningful. */
  isLoadingTrainerPrograms: boolean;
};

export const useTrainingPermissions = (): TrainingPermissions => {
  const { useCurrentUser } = getAppEnv();
  const currentUser = useCurrentUser((x: { value: unknown }) => x.value) as {
    id?: string;
    roles?: Record<string, string[]>;
    defaultAccountId?: string;
  } | null;
  const currentClient = useCurrentClient((x) => x.value);
  const { data: trainingPrograms, isLoading: isLoadingTrainerPrograms } = useMyTrainingPrograms();

  const permissions = useMemo<TrainingPermissions>(() => {
    const isTrainerOn = (trainingId: string): boolean => {
      return trainingPrograms?.includes(trainingId) || false;
    };

    const isTrainerOnEvent = (trainingEvent: TrainingEvent): boolean => {
      return trainingEvent.users.some((user) => user.userId === currentUser?.id && user.memberType === TrainingMemberType.Trainer);
    };

    const canCreateEvents = (trainingId: string): boolean => {
      return !!isTrainerOn(trainingId);
    };

    const canManageEvent = (trainingEvent: TrainingEvent): boolean => {
      return (
        canCreateEvents(trainingEvent.clientTrainingId) &&
        !trainingEvent.users.some((user) => user.userId === currentUser?.id && user.memberType === TrainingMemberType.Trainee)
      );
    };

    return {
      isTrainerOn,
      isTrainerOnEvent,
      canCreateEvents,
      canManageEvent,
      canManagePrograms: hasPermission(currentClient?.id, currentUser, Roles.Management),
      canViewReports: hasPermission(currentClient?.id, currentUser, Roles.TeamLead),
      isTrainerOnAny: (trainingPrograms?.length ?? 0) > 0,
      isLoadingTrainerPrograms,
    };
  }, [currentClient?.id, currentUser, trainingPrograms, isLoadingTrainerPrograms]);

  return permissions;
};
